import { t } from 'i18next';
import { durationToMinutes, splitDuration } from '../FanWave/constants';
import { GOALRUSH_BOUNDS, RULE_FIELDS, SESSION_DURATION_UNIT_MAX } from './constants';

// Form state helpers for the GoalRush rules. The draft holds what the admin typed (numbers as
// strings, so a half-typed value is not coerced); `typedLeaf` turns a leaf back into the API's
// shape and unit.

export const getLeaf = (obj, path) =>
  path.split('.').reduce((node, key) => (node == null ? undefined : node[key]), obj);

export const setLeaf = (obj, path, value) => {
  const [head, ...rest] = path.split('.');
  if (!rest.length) return { ...obj, [head]: value };
  return { ...obj, [head]: setLeaf(obj?.[head] || {}, rest.join('.'), value) };
};

const toNumber = (raw) => (raw === '' || raw == null ? undefined : Number(raw));

/** Resolved rules from the API → editable draft. */
export const toDraft = (rules) =>
  RULE_FIELDS.reduce((draft, { path, kind }) => {
    const value = getLeaf(rules, path);
    if (kind === 'duration') {
      // Seconds on the wire; the form shows the coarsest unit that divides them exactly.
      return setLeaf(draft, path, splitDuration(value == null ? NaN : value / 60));
    }
    if (kind === 'seconds') {
      return setLeaf(draft, path, value == null ? '' : String(value / 1000));
    }
    if (kind === 'int' || kind === 'decimal') {
      return setLeaf(draft, path, value == null ? '' : String(value));
    }
    return setLeaf(draft, path, value);
  }, {});

/** The typed value of one leaf of the draft, in the API's unit. */
export const typedLeaf = (draft, path, kind) => {
  const value = getLeaf(draft, path);
  if (kind === 'duration') {
    if (!value || value.value === '' || value.value == null) return undefined;
    return durationToMinutes(value.value, value.unit) * 60;
  }
  if (kind === 'seconds') {
    const seconds = toNumber(value);
    return seconds === undefined ? undefined : Math.round(seconds * 1000);
  }
  if (kind === 'int' || kind === 'decimal') return toNumber(value);
  return value;
};

/**
 * Only the leaves that differ from what the server returned, as a nested partial — the backend
 * writes exactly those, so an untouched field never becomes an override.
 */
export const diffRules = (initialDraft, draft) =>
  RULE_FIELDS.reduce((patch, { path, kind }) => {
    const before = typedLeaf(initialDraft, path, kind);
    const after = typedLeaf(draft, path, kind);
    return JSON.stringify(before) === JSON.stringify(after) ? patch : setLeaf(patch, path, after);
  }, {});

export const isEmptyPatch = (patch) => Object.keys(patch).length === 0;

const rangeError = (value, bound, integer = true) => {
  const { min, max } = GOALRUSH_BOUNDS[bound];
  if (value === undefined || Number.isNaN(value)) return t('admin.goalRush.errors.required');
  if (integer && !Number.isInteger(value)) return t('admin.goalRush.errors.integer');
  if (value < min || value > max) return t('admin.goalRush.errors.range', { min, max });
  return null;
};

/** `{path: message}` for everything the server would refuse (out-of-range values). */
export const validateRules = (draft) => {
  const errors = {};
  RULE_FIELDS.forEach(({ path, kind, bound }) => {
    if (kind === 'int' || kind === 'decimal') {
      const err = rangeError(typedLeaf(draft, path, kind), bound, kind === 'int');
      if (err) errors[path] = err;
    }
    if (kind === 'seconds') {
      const err = rangeError(toNumber(getLeaf(draft, path)), bound, false);
      if (err) errors[path] = err;
    }
    if (kind === 'duration') {
      // Checked in the unit entered: 1–60 minutes, 1–60 hours or 1–365 days.
      const entry = getLeaf(draft, path);
      const amount = toNumber(entry?.value);
      const unit = entry?.unit ?? 'minutes';
      const unitMax = SESSION_DURATION_UNIT_MAX[unit] ?? SESSION_DURATION_UNIT_MAX.minutes;
      const seconds = typedLeaf(draft, path, kind);
      const { min, max } = GOALRUSH_BOUNDS[bound];
      if (amount === undefined || Number.isNaN(amount)) {
        errors[path] = t('admin.goalRush.errors.required');
      } else if (!Number.isInteger(amount)) {
        errors[path] = t('admin.goalRush.errors.integer');
      } else if (amount < 1 || amount > unitMax || seconds < min || seconds > max) {
        errors[path] = t(`admin.goalRush.errors.durationRange.${unit}`, { max: unitMax });
      }
    }
  });
  return errors;
};

/** A refused save that names a field (`{err, field}`) → an inline error for that field. */
export const mapServerError = (res) => (res?.field && res?.err ? { [res.field]: res.err } : null);
